import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X } from "lucide-react";
import api, { mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ImageUpload from "../components/ImageUpload";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [robe, setRobe] = useState(null);

  useEffect(() => {
    api.get("/me/contributions").then((res) => setData(res.data));
    api.get("/kimono/catalog").then((res) => {
      const k = res.data.kimonos.find((x) => x.id === (user?.kimono || "chonin"));
      setRobe(k);
    });
  }, [user]);

  const onAvatar = async ({ url }) => {
    await api.put("/profile/avatar", { avatar_url: url });
    await refreshUser();
    toast.success("Portrait updated");
  };

  const removeAvatar = async () => {
    await api.delete("/profile/avatar");
    await refreshUser();
    toast.success("Portrait removed");
  };

  const avatar = mediaUrl(user?.avatar_url);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="profile-page">
      <div className="flex flex-wrap items-center gap-5 border-b border-border pb-10 mb-12">
        <div className="w-16 h-16 bg-primary text-primary-foreground flex items-center justify-center hanko text-2xl overflow-hidden shrink-0">
          {avatar ? (
            <img src={avatar} alt="Portrait" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
          ) : robe ? (
            <img src={robe.image} alt={robe.name} className="w-full h-full object-cover" />
          ) : (
            user?.name?.[0]?.toUpperCase() || "詩"
          )}
        </div>
        <div className="flex-1">
          <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight">{user?.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
          <div className="flex items-center gap-3 mt-3">
            <ImageUpload onUploaded={onAvatar} label={avatar ? "Change portrait" : "Add portrait"} testid="avatar-upload" />
            {avatar && (
              <button onClick={removeAvatar} data-testid="avatar-remove"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                <X className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
        </div>
        {robe && (
          <Link to="/kimono" data-testid="profile-robe" className="text-right group self-start">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Wearing</p>
            <p className="font-serif text-2xl group-hover:text-primary transition-colors">{robe.name}</p>
            <p className="text-xs text-secondary">{robe.caste}</p>
          </Link>
        )}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 gap-px bg-border border border-border mb-16">
            <div className="bg-background p-8">
              <p className="font-serif text-5xl text-primary" data-testid="stat-verses">{data.verse_count}</p>
              <p className="text-sm text-muted-foreground mt-2 uppercase tracking-widest">Verses linked</p>
            </div>
            <div className="bg-background p-8">
              <p className="font-serif text-5xl text-secondary" data-testid="stat-rooms">{data.rooms_count}</p>
              <p className="text-sm text-muted-foreground mt-2 uppercase tracking-widest">Rooms hosted</p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-8">
            <h2 className="font-serif text-3xl">Your verses</h2>
            <Link to="/hokku" data-testid="profile-hokku-link"
              className="inline-flex items-center gap-1.5 text-sm text-secondary hover:text-primary transition-colors">
              <span className="hanko">発句</span> Your Hokku collection →
            </Link>
          </div>
          {data.verses.length === 0 ? (
            <p className="text-muted-foreground">
              You have not linked any verses yet.{" "}
              <Link to="/rooms" className="text-primary border-b border-primary/40">Find a room</Link>.
            </p>
          ) : (
            <div className="space-y-8">
              {data.verses.map((v) => (
                <div key={v.id} className="flex flex-col sm:grid sm:grid-cols-12 gap-1.5 sm:gap-4" data-testid={`my-verse-${v.id}`}>
                  <div className="sm:col-span-9 border-l border-border pl-6">
                    <p className="font-poem text-xl whitespace-pre-line leading-relaxed">{v.text}</p>
                    <p className="mt-2 text-xs text-secondary">{v.verse_type}</p>
                  </div>
                  <div className="sm:col-span-3 pl-6 sm:pl-0 sm:text-right">
                    <Link to={`/rooms/${v.room_id}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      {v.room_title}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
